// src/contexts/InterviewContext.js
import React, { createContext, useState, useContext } from "react";

const InterviewContext = createContext();

export const useInterviewContext = () => useContext(InterviewContext);

export const InterviewProvider = ({ children }) => {
  const [cvData, setCvData] = useState({
    rawText: null,
    extractedInfo: null,
    fileName: null,
    jobDescription: null,
    sessionId: null, // Added: To store the session ID
  });
  const [questions, setQuestions] = useState([]); // Expects array of { id, question, type, ... }
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  // userResponses stores: { question: { id, question, type }, answerText, audioUrl, feedback: { parsed AiFeedbackJson } }
  const [userResponses, setUserResponses] = useState([]);
  const [isLoading, setIsLoading] = useState({
    cvAnalysis: false,
    questionGeneration: false,
    transcription: false,
    evaluation: false,
    overallFeedback: false,
  });
  const [error, setError] = useState(null);
  const [interviewStage, setInterviewStage] = useState("cv_upload"); // cv_upload, setup_generating_questions, active, finished

  const [overallFeedbackText, setOverallFeedbackText] = useState(null);
  const [overallFeedbackError, setOverallFeedbackError] = useState(null);

  const resetInterview = () => {
    setCvData({
      rawText: null,
      extractedInfo: null,
      fileName: null,
      jobDescription: null,
      sessionId: null, // Reset session ID
    });
    setQuestions([]);
    setCurrentQuestionIndex(0);
    setUserResponses([]);
    setError(null);
    setInterviewStage("cv_upload");
    setOverallFeedbackText(null);
    setOverallFeedbackError(null);
    setIsLoading({
      cvAnalysis: false,
      questionGeneration: false,
      transcription: false,
      evaluation: false,
      overallFeedback: false,
    });
  };

  const addResponse = (questionObject, answerText, audioUrl) => {
    setUserResponses((prev) => [
      ...prev,
      { question: questionObject, answerText, audioUrl, feedback: null },
    ]);
  };

  const addFeedbackToLastResponse = (feedback) => {
    setUserResponses((prev) => {
      const updatedResponses = [...prev];
      if (updatedResponses.length > 0) {
        updatedResponses[updatedResponses.length - 1].feedback = feedback;
      }
      return updatedResponses;
    });
  };

  const value = {
    cvData,
    setCvData,
    questions,
    setQuestions,
    currentQuestionIndex,
    setCurrentQuestionIndex,
    userResponses,
    // setUserResponses, // Usually not exposed directly if specific adders are provided
    addResponse,
    addFeedbackToLastResponse,
    isLoading,
    setIsLoading,
    error,
    setError,
    interviewStage,
    setInterviewStage,
    resetInterview,
    overallFeedbackText,
    setOverallFeedbackText,
    overallFeedbackError,
    setOverallFeedbackError,
    clearGlobalError: () => setError(null),
  };

  return (
    <InterviewContext.Provider value={value}>
      {children}
    </InterviewContext.Provider>
  );
};
